import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import { Field, CheckRow, BigButton, inputCls, textareaCls, GkkOptions } from "@/components/FormBits";
import { api, errMsg } from "@/lib/api";
import { VOLUNTEER_ACTIVITIES, SPECIAL_GKK } from "@/lib/constants";

const OFFERS = { volunteer: "Volunteer my time", resources: "Contribute resources", professional: "Offer professional assistance" };

export default function Volunteer() {
  const [f, setF] = useState({ full_name: "", contact_number: "", email: "", gkk: "", activities: [], availability: "", qualifications: "", offer_type: "volunteer", consent_privacy: false });
  const [gkks, setGkks] = useState([]);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((o) => ({ ...o, ...p }));
  useEffect(() => { api.get("/public/gkks").then((r) => setGkks(r.data)).catch(() => setGkks([])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.gkk || f.activities.length === 0 || !f.consent_privacy) { toast.error("Please complete the required fields and the privacy acknowledgment."); return; }
    setBusy(true);
    try { await api.post("/public/volunteers", { ...f, email: f.email || null, contact_number: f.contact_number || null }); setDone(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        {done ? (
          <div data-testid="volunteer-confirmation" className="fade-up">
            <h1 className="text-3xl sm:text-4xl font-black">Thank you for your generosity.</h1>
            <p className="mt-4 text-lg">Your offer has been received. The parish will contact you to verify identity, suitability, qualifications and safeguarding requirements before any participation is authorized.</p>
            <Link to="/" className="inline-block mt-8"><BigButton type="button" data-testid="volunteer-home-button">Back to home</BigButton></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="volunteer-form">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black">I want to help</h1>
              <p className="mt-3 text-[#605349] text-base md:text-lg">Offer your time, resources, or professional skills. Registration does not mean automatic approval.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-2.5">
              {Object.entries(OFFERS).map(([k, v]) => <CheckRow key={k} testid={`offer-${k}`} checked={f.offer_type === k} onChange={() => set({ offer_type: k })} label={v} />)}
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2"><Field label="Full name" required htmlFor="vn"><input id="vn" data-testid="volunteer-input-name" className={inputCls} value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} /></Field></div>
              <Field label="Contact number" htmlFor="vc"><input id="vc" data-testid="volunteer-input-contact" inputMode="tel" className={inputCls} value={f.contact_number} onChange={(e) => set({ contact_number: e.target.value })} /></Field>
              <Field label="Email" htmlFor="ve"><input id="ve" data-testid="volunteer-input-email" type="email" className={inputCls} value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
              <div className="sm:col-span-2"><Field label="GKK or community" required htmlFor="vg">
                <select id="vg" data-testid="volunteer-select-gkk" className={inputCls} value={f.gkk} onChange={(e) => set({ gkk: e.target.value })}>
                  <option value="">Choose…</option><GkkOptions gkks={gkks} /><optgroup label="Other">{SPECIAL_GKK.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</optgroup>
                </select></Field></div>
            </div>
            <fieldset>
              <legend className="font-bold mb-3">How would you like to help? <span className="text-[#B85028]">*</span></legend>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {Object.entries(VOLUNTEER_ACTIVITIES).map(([k, v]) => (
                  <CheckRow key={k} testid={`activity-${k}`} checked={f.activities.includes(k)} label={v}
                    onChange={(on) => set({ activities: on ? [...f.activities, k] : f.activities.filter((a) => a !== k) })} />
                ))}
              </div>
            </fieldset>
            <Field label="Availability" htmlFor="va"><input id="va" data-testid="volunteer-input-availability" className={inputCls} placeholder="e.g. Saturday mornings" value={f.availability} onChange={(e) => set({ availability: e.target.value })} /></Field>
            <Field label="Relevant qualifications (only if offering professional services)" htmlFor="vq"><textarea id="vq" data-testid="volunteer-input-qualifications" className={textareaCls} value={f.qualifications} onChange={(e) => set({ qualifications: e.target.value })} /></Field>
            <CheckRow testid="volunteer-consent-privacy" checked={f.consent_privacy} onChange={(v) => set({ consent_privacy: v })} label="I have read the privacy notice (required)" sub={<a href="/privacy" target="_blank" rel="noreferrer" className="underline">Open privacy notice</a>} />
            <BigButton type="submit" variant="green" data-testid="volunteer-submit-button" disabled={busy}>{busy ? "Sending…" : "Send my offer"}</BigButton>
          </form>
        )}
      </div>
    </PublicLayout>
  );
}
